import React, { useState, useEffect } from 'react';
import {
  Spin,
  Dropdown,
  Menu,
} from 'antd';
import {
  MessageOutlined,
  BarChartOutlined,
  BookOutlined,
  RightOutlined,
  DownOutlined,
  DeleteOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  ShareAltOutlined,
  UndoOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Workspace, SavedWorkspaceState } from '../../types';
import { useWorkspaceStore } from '../../store/workspace';
import { useTabStore } from '../../store/tabs';
import { DeleteSavedStateDialog, PropertiesDialog, RenameDialog } from '../Dialogs';
import { apiService } from '../../services/api';

interface WorkspaceTreeProps {
  onWorkspaceSelect: (workspace: Workspace) => void;
  onSavedStateRestore: (workspace: Workspace, savedState: SavedWorkspaceState) => void;
}

export const WorkspaceTree: React.FC<WorkspaceTreeProps> = ({
  onWorkspaceSelect,
  onSavedStateRestore,
}) => {
  const { workspaces, isLoading, error, loadWorkspaces } = useWorkspaceStore();
  const { tabs, addTab, setActiveTab, forceCloseTab } = useTabStore((state) => ({
    tabs: state.tabs,
    addTab: state.addTab,
    setActiveTab: state.setActiveTab,
    forceCloseTab: state.forceCloseTab
  }));
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set());
  const [savedStates, setSavedStates] = useState<SavedWorkspaceState[]>([]);
  const [isLoadingSavedStates, setIsLoadingSavedStates] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    savedState: SavedWorkspaceState | null;
  }>({ isOpen: false, savedState: null });
  
  const [propertiesDialog, setPropertiesDialog] = useState<{
    isOpen: boolean;
    savedState: SavedWorkspaceState | null;
  }>({ isOpen: false, savedState: null });
  
  const [renameDialog, setRenameDialog] = useState<{
    isOpen: boolean;
    savedState: SavedWorkspaceState | null;
  }>({ isOpen: false, savedState: null });

  useEffect(() => {
    // Load workspaces and saved states in parallel for better performance
    const loadInParallel = async () => {
      console.log('Loading workspace tree data in parallel');
      try {
        await Promise.all([
          loadWorkspaces(),
          loadSavedStates()
        ]);
        console.log('Parallel workspace tree loading completed');
      } catch (error) {
        console.error('Failed to load workspace tree data:', error);
      }
    };
    
    loadInParallel();
  }, [loadWorkspaces]);


  // Listen for custom events to refresh saved states
  useEffect(() => {
    const handleCustomSaveEvent = () => {
      loadSavedStates();
    };
    
    window.addEventListener('workspaceSavedStatesChanged', handleCustomSaveEvent);

    return () => {
      window.removeEventListener('workspaceSavedStatesChanged', handleCustomSaveEvent);
    };
  }, []);

  const loadSavedStates = async () => {
    try {
      setIsLoadingSavedStates(true);
      const states = await apiService.getSavedStates();
      setSavedStates(states);
    } catch (error) {
      console.error('Failed to load saved states:', error);
      setSavedStates([]);
    } finally {
      setIsLoadingSavedStates(false);
    }
  };

  const getDomainIcon = (domain?: string) => {
    switch (domain) {
      case 'coding':
        return <MessageOutlined style={{ fontSize: '16px' }} />;
      case 'analytics':
        return <BarChartOutlined style={{ fontSize: '16px' }} />;
      case 'reasoning':
        return <BookOutlined style={{ fontSize: '16px' }} />;
      default:
        return null; // No icon for unknown domains
    }
  };

  const toggleWorkspace = (workspaceId: string) => {
    const newExpanded = new Set(expandedWorkspaces);
    if (newExpanded.has(workspaceId)) {
      newExpanded.delete(workspaceId);
    } else {
      newExpanded.add(workspaceId);
    }
    setExpandedWorkspaces(newExpanded);
  };

  // Count workspace instances (tabs with the same workspace id)
  const getWorkspaceInstanceCount = (workspaceId: string) => {
    return tabs.filter(tab => tab.type === 'workspace' && tab.workspace?.id === workspaceId).length;
  };

  // Check if max instances reached
  const isMaxInstancesReached = (workspace: Workspace) => {
    if (!workspace.maxInstances || workspace.maxInstances === 0) return false; // 0 means unlimited
    return getWorkspaceInstanceCount(workspace.id) >= workspace.maxInstances;
  };

  // Handle workspace name click - toggle expansion if children exist, create instance if no children
  const handleWorkspaceNameClick = (workspace: Workspace) => {
    const workspaceSavedStates = getWorkspaceSavedStates(workspace.id);
    
    if (workspaceSavedStates.length > 0) {
      // Has children - toggle expansion
      toggleWorkspace(workspace.id);
    } else {
      // No children - create new instance (same as plus button)
      handleCreateInstance(workspace);
    }
  };

  // Handle plus button click - create new workspace instance
  const handleCreateInstance = (workspace: Workspace) => {
    // If workspace only allows 1 instance, check if there's already an unsaved tab
    if (workspace.maxInstances === 1) {
      const existingUnsavedTab = tabs.find(tab => 
        tab.type === 'workspace' && 
        tab.workspace?.id === workspace.id && 
        !tab.savedStateId
      );
      
      if (existingUnsavedTab) {
        // Switch to existing unsaved tab for single-instance workspaces
        setActiveTab(existingUnsavedTab.id);
        return;
      }
    }
    
    // For multi-instance workspaces or when no existing unsaved tab, create new tab
    onWorkspaceSelect(workspace);
  };

  const handleWorkspaceClick = (workspace: Workspace) => {
    onWorkspaceSelect(workspace);
  };

  const handleSavedStateClick = (workspace: Workspace, savedState: SavedWorkspaceState) => {
    onSavedStateRestore(workspace, savedState);
  };

  const handleMenuAction = (action: string, savedState: SavedWorkspaceState, workspace: Workspace) => {
    switch (action) {
      case 'restore':
        onSavedStateRestore(workspace, savedState);
        break;
      case 'properties':
        setPropertiesDialog({ isOpen: true, savedState });
        break;
      case 'rename':
        setRenameDialog({ isOpen: true, savedState });
        break;
      case 'share':
        handleShare(savedState);
        break;
      case 'delete':
        setDeleteDialog({ isOpen: true, savedState });
        break;
    }
  };

  const handleShare = (savedState: SavedWorkspaceState) => {
    // Copy saved state info to clipboard
    const shareData = {
      name: savedState.name,
      workspace: savedState.workspaceId,
      created: savedState.createdAt.toLocaleDateString(),
      updated: savedState.updatedAt.toLocaleDateString(),
    };
    
    navigator.clipboard.writeText(JSON.stringify(shareData, null, 2)).then(() => {
      // Could show a toast notification here
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
  };

  const handleRename = async (newName: string) => {
    if (!renameDialog.savedState) return;

    try {
      await apiService.updateSavedState(renameDialog.savedState.id, { name: newName });
      
      // Refresh saved states
      loadSavedStates();
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('workspaceSavedStatesChanged'));
      
      setRenameDialog({ isOpen: false, savedState: null });
    } catch (error) {
      console.error('Failed to rename saved state:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.savedState) return;

    try {
      const deletedSavedStateId = deleteDialog.savedState.id;
      await apiService.deleteSavedState(deletedSavedStateId);
      
      // Close any tabs that were opened from this saved state
      tabs.forEach(tab => {
        if (tab.savedStateId === deletedSavedStateId) {
          forceCloseTab(tab.id);
        }
      });
      
      // Dispatch custom event to notify other components to refresh
      window.dispatchEvent(new CustomEvent('workspaceSavedStatesChanged'));
      
      // Refresh local state
      loadSavedStates();
      
      // Close dialog
      setDeleteDialog({ isOpen: false, savedState: null });
    } catch (error) {
      console.error('Failed to delete saved state:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, savedState: null });
  };

  const handlePropertiesClose = () => {
    setPropertiesDialog({ isOpen: false, savedState: null });
  };

  const handleRenameCancel = () => {
    setRenameDialog({ isOpen: false, savedState: null });
  };

  const getWorkspaceSavedStates = (workspaceId: string) => {
    return savedStates
      .filter(state => state.workspaceId === workspaceId)
      .sort((a, b) => {
        // Use createdAt for sorting, with fallback handling
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '1rem' }}>
        <Spin tip="Loading workspaces..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem' }}>
        <div style={{ color: 'var(--color-error)', fontSize: '0.875rem' }}>
          Error loading workspaces: {error}
        </div>
        <button 
          onClick={loadWorkspaces}
          style={{ 
            marginTop: '0.5rem',
            background: 'none',
            border: '1px solid var(--color-border-secondary)',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="workspace-tree">
        <div style={{ padding: '0.5rem 0' }}>
        
        {workspaces.map((workspace) => {
          const isExpanded = expandedWorkspaces.has(workspace.id);
          const workspaceSavedStates = getWorkspaceSavedStates(workspace.id);
          const maxInstancesReached = isMaxInstancesReached(workspace);
          
          
          return (
            <div key={workspace.id} className="workspace-node">
              {/* Workspace Item */}
              <div 
                className="workspace-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  margin: '0 0.5rem',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {/* Expand/Collapse button with consistent width */}
                <div style={{ 
                  width: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  marginRight: '0.5rem'
                }}>
                  {workspaceSavedStates.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleWorkspace(workspace.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '2px',
                        cursor: 'pointer',
                        color: 'var(--color-text-secondary)',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {isExpanded ? <DownOutlined style={{ fontSize: '16px' }} /> : <RightOutlined style={{ fontSize: '16px' }} />}
                    </button>
                  )}
                </div>
                
                {/* Workspace content - clickable to toggle/create instance */}
                <div
                  onClick={() => handleWorkspaceNameClick(workspace)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flex: 1,
                    minWidth: 0,
                    cursor: 'pointer',
                  }}
                >
                  {getDomainIcon(workspace.settings.domain)}
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--color-text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {workspace.name}
                  </span>
                </div>

                {/* Plus button to create new instance */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!maxInstancesReached) {
                      handleCreateInstance(workspace);
                    }
                  }}
                  disabled={maxInstancesReached}
                  style={{
                    background: maxInstancesReached ? 'var(--color-text-disabled)' : 'var(--color-primary)',
                    border: maxInstancesReached ? '1px solid var(--color-border-primary)' : 'none',
                    padding: '0',
                    cursor: maxInstancesReached ? 'not-allowed' : 'pointer',
                    color: maxInstancesReached ? 'var(--color-text-secondary)' : 'var(--color-bg-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '0.5rem',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    transition: maxInstancesReached ? 'none' : 'all 0.2s ease',
                    opacity: maxInstancesReached ? 0.7 : 1,
                    pointerEvents: maxInstancesReached ? 'none' : 'auto',
                  }}
                  title={maxInstancesReached 
                    ? `Maximum instances (${workspace.maxInstances}) reached` 
                    : 'Create new workspace instance'
                  }
                  onMouseEnter={maxInstancesReached ? undefined : (e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-primary-active)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={maxInstancesReached ? undefined : (e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <PlusOutlined style={{ fontSize: '14px' }} />
                </button>
              </div>

              {/* Saved States */}
              {isExpanded && workspaceSavedStates.length > 0 && (
                <div className="saved-states">
                  {workspaceSavedStates.map((savedState) => (
                    <div
                      key={savedState.id}
                      className="saved-state-item"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.375rem 1rem',
                        borderRadius: '4px',
                        margin: '0 0.5rem',
                        transition: 'background-color 0.2s ease',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                        const menuBtn = e.currentTarget.querySelector('.menu-button') as HTMLElement;
                        if (menuBtn) menuBtn.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        const menuBtn = e.currentTarget.querySelector('.menu-button') as HTMLElement;
                        if (menuBtn) menuBtn.style.opacity = '0';
                      }}
                    >
                      {/* Alignment spacer to match workspace layout - expand button + icon */}
                      <div style={{ width: '20px', marginRight: '0.5rem' }}>
                      </div>
                      {/* Only add icon spacing if the parent workspace has an icon */}
                      {getDomainIcon(workspace.settings.domain) && (
                        <div style={{ width: '16px', marginRight: '0.5rem' }}> {/* Space for domain icon alignment */}
                        </div>
                      )}
                      <div 
                        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={() => handleSavedStateClick(workspace, savedState)}
                      >
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          color: 'var(--color-text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {savedState.name}
                        </div>
                        <div style={{
                          fontSize: '0.675rem',
                          color: 'var(--color-text-tertiary)',
                        }}>
                          {formatTimestamp(savedState.updatedAt)}
                        </div>
                      </div>
                      <div
                        className="menu-button"
                        style={{
                          opacity: '0',
                          transition: 'opacity 0.2s ease',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: 'restore',
                                label: 'Restore',
                                icon: <UndoOutlined />,
                                onClick: () => handleMenuAction('restore', savedState, workspace)
                              },
                              {
                                key: 'properties',
                                label: 'Properties',
                                icon: <EyeOutlined />,
                                onClick: () => handleMenuAction('properties', savedState, workspace)
                              },
                              {
                                key: 'share',
                                label: 'Share',
                                icon: <ShareAltOutlined />,
                                onClick: () => handleMenuAction('share', savedState, workspace)
                              },
                              {
                                key: 'rename',
                                label: 'Rename',
                                icon: <EditOutlined />,
                                onClick: () => handleMenuAction('rename', savedState, workspace)
                              },
                              {
                                type: 'divider'
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                icon: <DeleteOutlined />,
                                danger: true,
                                onClick: () => handleMenuAction('delete', savedState, workspace)
                              }
                            ]
                          }}
                          trigger={['click']}
                          placement="bottomRight"
                        >
                          <MoreOutlined style={{ fontSize: '16px', cursor: 'pointer', padding: '4px' }} />
                        </Dropdown>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteSavedStateDialog
        isOpen={deleteDialog.isOpen}
        savedState={deleteDialog.savedState}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Properties Dialog */}
      <PropertiesDialog
        isOpen={propertiesDialog.isOpen}
        savedState={propertiesDialog.savedState}
        onClose={handlePropertiesClose}
      />

      {/* Rename Dialog */}
      <RenameDialog
        isOpen={renameDialog.isOpen}
        savedState={renameDialog.savedState}
        onConfirm={handleRename}
        onCancel={handleRenameCancel}
      />
    </>
  );
};